import { TZDate } from '@date-fns/tz';
import { DeliveryWindowDateRecord, DeliveryWindowOutputRecord } from '@navio/nsdk';
import { addDays, isAfter, isBefore } from 'date-fns';
import { CustomDateFormats, isToday, isTomorrow, NoddiDateFormats, noddiFormatDate, parseDate } from 'noddi-util';

/** Days per page on every screen size: tabs on a phone, side-by-side columns from `md` up. */
export const TIME_WINDOW_DAYS_PER_PAGE = 5;

/**
 * Text of anything out of play: a sold-out window, a fully booked day, a page button at the end of the
 * range. Renders the handover's #a1a1a1 on the white card. Below AA on purpose, so it reads as spent
 * next to what is bookable.
 */
export const UNAVAILABLE_TEXT = 'text-muted-foreground/67';

/**
 * The same, for the phone picker sitting straight on the page background: the handover's #86739c on
 * the page gradient, a shade lighter on the day tabs' white card. A brand tint rather than grey, which
 * reads as a dirty patch on a colored page.
 */
export const UNAVAILABLE_TEXT_ON_PAGE = 'text-primary/57';

/**
 * Where the picker is drawn. `page`: below `md` it drops its card and sits on the page background, as
 * on the customer app's booking screens. `card`: a card on every screen, e.g. inside a white dialog.
 */
export type TimeWindowPickerSurface = 'card' | 'page';

export const isTimeWindowUnavailable = ({ timeWindow }: { timeWindow: DeliveryWindowOutputRecord }) =>
  timeWindow.isTimeWindowTooShort || timeWindow.isClosed;

export const parseTimeSlotFromPublicTimes = ({
  startsAt,
  endsAt,
  timeZone
}: {
  startsAt: string;
  endsAt: string;
  timeZone: { value: string; label: string };
}) => {
  const startLocal = new TZDate(startsAt, timeZone.value);
  const endLocal = new TZDate(endsAt, timeZone.value);

  const startHour = noddiFormatDate(startLocal, CustomDateFormats.HOURS);
  const endHour = noddiFormatDate(endLocal, CustomDateFormats.HOURS);

  return `${startHour.slice(0, 2)}–${endHour.slice(0, 2)}`;
};

export const getWeekdayLabel = (day: Date, translations: { today: string; tomorrow: string }) => {
  if (isToday(day)) {
    return translations.today;
  }
  if (isTomorrow(day)) {
    return translations.tomorrow;
  }
  return noddiFormatDate(day, CustomDateFormats.SHORT_WEEKDAY);
};

/** Spoken day for a slot, the same words as the header: "Today 16" or "Mon 16". */
export const getDeliveryDayLabel = (date: string, translations: { today: string; tomorrow: string }) => {
  const day = parseDate(date, NoddiDateFormats.API_DATE);
  return `${getWeekdayLabel(day, translations)} ${noddiFormatDate(day, CustomDateFormats.DAY_OF_MONTH)}`;
};

/** Windows the day offers at all; a slot without one is not on sale that day, not sold out. */
export const getDeliveryWindows = (dateRecord: DeliveryWindowDateRecord) =>
  dateRecord.timeSlots.flatMap(({ deliveryWindow }) => (deliveryWindow ? [deliveryWindow] : []));

export const countBookableTimeWindows = (dateRecord: DeliveryWindowDateRecord) =>
  getDeliveryWindows(dateRecord).filter((timeWindow) => !isTimeWindowUnavailable({ timeWindow })).length;

export const isSameDeliveryWindow = (
  timeWindow: Pick<DeliveryWindowOutputRecord, 'startsAt' | 'endsAt'>,
  selectedDeliveryWindow?: { startsAt: string; endsAt: string }
) =>
  !!selectedDeliveryWindow &&
  selectedDeliveryWindow.startsAt === timeWindow.startsAt &&
  selectedDeliveryWindow.endsAt === timeWindow.endsAt;

export const getFromAndToDate = ({
  date,
  page,
  pageSize,
  maxDate,
  firstAvailableDate
}: {
  date: TZDate;
  page: number;
  pageSize: number;
  maxDate: TZDate;
  firstAvailableDate: TZDate;
}) => {
  // eslint-disable-next-line no-restricted-syntax -- clamped to firstAvailableDate after the initial computation
  let calculatedFromDate = addDays(date, page * pageSize);

  // Ensure fromDate is not before firstAvailableDate
  if (isBefore(calculatedFromDate, firstAvailableDate)) {
    calculatedFromDate = firstAvailableDate;
  }

  // Calculate potential to date
  // eslint-disable-next-line no-restricted-syntax -- clamped to maxDate after the initial computation
  let calculatedToDate = addDays(calculatedFromDate, pageSize - 1);

  // If the calculated to date exceeds the max date
  if (isAfter(calculatedToDate, maxDate)) {
    calculatedToDate = maxDate;

    // Adjust the from date to show a full page if possible
    const daysToMax = Math.floor((maxDate.getTime() - firstAvailableDate.getTime()) / (24 * 60 * 60 * 1000));

    // If there are enough days between firstAvailableDate and maxToDate for a full page
    if (daysToMax >= pageSize - 1) {
      calculatedFromDate = addDays(maxDate, -(pageSize - 1));
    }
  }

  return {
    fromDate: calculatedFromDate,
    toDate: calculatedToDate
  };
};
